"use client";

import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useRouter } from "next/navigation";
import * as NavigationMenuPrimitive from "@radix-ui/react-navigation-menu";
import { cva } from "class-variance-authority";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";
import type { StaticNavLink } from "@/config/nav-links";
import NavMegaMenu from "./NavMegaMenu";
import MarketsMegaMenu from "./MarketsMegaMenu";
import { useNavTheme, useNavThemeStyles } from "./nav-theme-context";
import TrackedLink from "./TrackedLink";
import { useVisibleStaticNavLinks } from "./nav-profile";
import { useNavSearch } from "./use-nav-search";
import NavSearchField from "./NavSearchField";
import NavSearchPanel from "./NavSearchPanel";
import { getNavSearchResults } from "./nav-search-data";

const navigationMenuTriggerStyle = cva(
    "group inline-flex h-auto w-max items-center justify-center bg-transparent text-base font-regular leading-[22px] transition-colors hover:opacity-80 focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=open]:font-bold",
);

type StaticNavLinksProps = {
    links: StaticNavLink[];
    logo: ReactNode;
    children?: ReactNode;
};

const isStandaloneLink = (link: StaticNavLink): link is StaticNavLink & { href: string } =>
    Boolean(link.href) && !link.sections?.length && !link.categories?.length;

const DESKTOP_SEARCH_PANEL_ID = "desktop-nav-search-panel";

const searchTriggerFocus = {
    white: "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-button-primary focus-visible:ring-offset-2 focus-visible:ring-offset-white",
    blue: "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-nav-bg",
} as const;

const StaticNavLinks = ({ links, logo, children }: StaticNavLinksProps) => {
    const theme = useNavTheme();
    const styles = useNavThemeStyles();
    const router = useRouter();
    const rootRef = useRef<HTMLElement>(null);
    const [openNavItem, setOpenNavItem] = useState("");
    const visibleLinks = useVisibleStaticNavLinks(links);
    const search = useNavSearch();
    const tone = theme === "white" ? "light" : "dark";

    const openDesktopSearch = () => {
        setOpenNavItem("");
        search.openSearch();
    };

    const goToFirstResult = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const first = getNavSearchResults(search.category, search.query)[0];
        if (!first) return;
        search.closeSearch();
        if (first.openInNewTab) {
            window.open(first.href, "_blank", "noopener,noreferrer");
            return;
        }
        if (first.href.startsWith("http")) {
            window.location.assign(first.href);
            return;
        }
        router.push(first.href);
    };

    const searchOpen = search.open;
    const closeSearch = search.closeSearch;

    useEffect(() => {
        if (!searchOpen) return;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                event.preventDefault();
                closeSearch({ restoreFocus: true });
            }
        };
        const onPointerDown = (event: MouseEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) {
                closeSearch();
            }
        };

        document.addEventListener("keydown", onKeyDown);
        document.addEventListener("mousedown", onPointerDown);
        return () => {
            document.removeEventListener("keydown", onKeyDown);
            document.removeEventListener("mousedown", onPointerDown);
        };
    }, [closeSearch, searchOpen]);

    return (
        <NavigationMenuPrimitive.Root
            ref={rootRef}
            className="relative hidden h-full w-full px-5 sm:flex sm:items-center"
            value={search.open ? "" : openNavItem}
            onValueChange={(next) => {
                if (next) search.closeSearch();
                setOpenNavItem(next);
            }}
        >
            <div className="flex h-full min-w-0 flex-1 items-center gap-6 min-[1100px]:gap-8 min-[1280px]:gap-[60px]">
                <div className="shrink-0">{logo}</div>
                <div className="relative flex h-full min-w-0 flex-1 items-center">
                    {search.open ? (
                        <NavSearchField
                            tone={tone}
                            query={search.query}
                            placeholder="Search products on InCred Money"
                            panelId={DESKTOP_SEARCH_PANEL_ID}
                            inputRef={search.inputRef}
                            onQueryChange={search.setQuery}
                            onClearOrClose={search.clearOrClose}
                            onSubmit={goToFirstResult}
                        />
                    ) : (
                        <NavigationMenuPrimitive.List className="group flex min-w-0 list-none items-center gap-4 min-[1100px]:gap-6 min-[1280px]:gap-9">
                    {visibleLinks.map((link) => (
                        <NavigationMenuPrimitive.Item key={link.label} value={link.label}>
                            {isStandaloneLink(link) ? (
                                <NavigationMenuPrimitive.Link asChild>
                                    <TrackedLink
                                        href={link.href}
                                        event={link.eventName}
                                        cleverTapEvent={link.branchEvent}
                                        target={link.openInNewTab ? "_blank" : undefined}
                                        rel={
                                            link.openInNewTab
                                                ? "noopener noreferrer"
                                                : undefined
                                        }
                                        className={cn(
                                            navigationMenuTriggerStyle(),
                                            "no-underline",
                                            styles.link,
                                        )}
                                    >
                                        {link.label}
                                    </TrackedLink>
                                </NavigationMenuPrimitive.Link>
                            ) : (
                                <>
                                    <NavigationMenuPrimitive.Trigger
                                        className={cn(
                                            navigationMenuTriggerStyle(),
                                            "group",
                                            styles.link,
                                            theme === "blue"
                                                ? "data-[state=open]:text-[#FBCBAD]"
                                                : "data-[state=open]:text-brandAccent",
                                        )}
                                    >
                                        {link.label}
                                    </NavigationMenuPrimitive.Trigger>
                                    <NavigationMenuPrimitive.Content className="w-full data-[motion^=from-]:animate-in data-[motion^=to-]:animate-out data-[motion^=from-]:fade-in data-[motion^=to-]:fade-out data-[motion=from-end]:slide-in-from-right-52 data-[motion=from-start]:slide-in-from-left-52 data-[motion=to-end]:slide-out-to-right-52 data-[motion=to-start]:slide-out-to-left-52">
                                        {link.layout === "sidebar" && link.categories ? (
                                            <MarketsMegaMenu
                                                sidebarTitle={link.sidebarTitle}
                                                categories={link.categories}
                                                isOpen={openNavItem === link.label}
                                            />
                                        ) : (
                                            <NavMegaMenu sections={link.sections ?? []} />
                                        )}
                                    </NavigationMenuPrimitive.Content>
                                </>
                            )}
                        </NavigationMenuPrimitive.Item>
                    ))}
                        </NavigationMenuPrimitive.List>
                    )}
                    {search.open ? (
                        <div className="absolute left-0 top-full z-30 w-full max-w-[636px]">
                            <NavSearchPanel
                                tone={tone}
                                variant="desktop"
                                panelId={DESKTOP_SEARCH_PANEL_ID}
                                query={search.query}
                                category={search.category}
                                onCategoryChange={search.setCategory}
                                onNavigate={() => search.closeSearch()}
                            />
                        </div>
                    ) : null}
                </div>
            </div>

            <div className="ml-4 flex shrink-0 items-center gap-4">
                {search.open ? null : (
                    <button
                        ref={search.triggerRef}
                        type="button"
                        className={cn(
                            "inline-flex size-6 items-center justify-center rounded-sm",
                            styles.search,
                            searchTriggerFocus[theme],
                        )}
                        aria-label="Search"
                        aria-expanded={search.open}
                        aria-controls={DESKTOP_SEARCH_PANEL_ID}
                        onClick={openDesktopSearch}
                    >
                        <Search className="size-6" strokeWidth={1.5} aria-hidden />
                    </button>
                )}
                {children}
            </div>

            <div className="absolute inset-x-0 top-full z-20 w-full">
                <NavigationMenuPrimitive.Viewport
                    className={cn(
                        "h-[var(--radix-navigation-menu-viewport-height)] !w-full overflow-hidden rounded-b-2xl shadow-[0_4px_15px_0_rgba(0,0,0,0.07)] data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-90",
                        // Learn is a single row — let viewport height follow content; keep min-h for Products/Markets.
                        openNavItem === "Learn" ? "min-h-0" : "min-h-[300px]",
                        styles.viewport,
                    )}
                    style={{ width: "100%" }}
                />
            </div>
        </NavigationMenuPrimitive.Root>
    );
};

export default StaticNavLinks;
